import { deriveAll, parsePrivKey } from './game/crypto.js';
import { spin } from './game/spin.js';
import { fetchAccountInfo, probeLedgerIndex, XRPL_RPC_HOST } from './game/xrpl-api.js';
import { Log } from './ui/log.js';
import { ClassicReels } from './ui/slot-classic.js';
import { RealisticReels } from './ui/slot-realistic.js';
import { WinDialog } from './ui/win-dialog.js';
import { sfx, setMuted, unlock } from './audio/audio.js';

const ANIMATION_MIN_MS = 1100;
const DROPS_PER_XRP = 1_000_000n;

function formatNumber(value) {
  return value.toLocaleString('en-US');
}

function formatXrp(drops) {
  const whole = drops / DROPS_PER_XRP;
  const fraction = (drops % DROPS_PER_XRP).toString().padStart(6, '0');
  return `${whole.toString()}.${fraction}`;
}

function shorten(value, chars = 8) {
  if (value.length <= chars * 2 + 3) return value;
  return `${value.slice(0, chars)}…${value.slice(-chars)}`;
}

function renderStaticHeader() {
  document.getElementById('tagline').textContent =
    'Roll a live XRPL account.';
  document.getElementById('stat-network').textContent = 'XRPL Mainnet';
  document.getElementById('stat-network-sub').textContent =
    'Ed25519 private keys';
  document.getElementById('stat-rpc').textContent = XRPL_RPC_HOST;
  document.getElementById('stat-rpc-sub').textContent =
    'public JSON-RPC endpoint';
  document.getElementById('stat-ledger').textContent = 'Checking…';
  document.getElementById('stat-ledger-sub').textContent =
    'validated ledger index';
  document.getElementById('stat-rule').textContent = 'Activated account';
  document.getElementById('stat-rule-sub').textContent =
    'live XRP balance from account_info';
}

function setLedgerIndex(ledgerIndex) {
  if (ledgerIndex === null || ledgerIndex === undefined) return;
  document.getElementById('stat-ledger').textContent = formatNumber(ledgerIndex);
}

async function main() {
  renderStaticHeader();

  const log = new Log(document.getElementById('log'));
  log.append(`Connected mode: XRPL Mainnet via ${XRPL_RPC_HOST}.`);
  log.append('Win rule: the generated classic address must already be activated.');
  log.append('Wallet algorithm: Ed25519.');
  log.append('Pull the lever.');

  const classic = new ClassicReels(document.getElementById('reels-classic'));
  const realistic = new RealisticReels(
    document.getElementById('reels-realistic')
  );
  classic.show();
  realistic.hide();

  const winDialog = new WinDialog(document.getElementById('win-dialog'));

  const pullBtn = document.getElementById('pull-btn');
  const realisticToggle = document.getElementById('toggle-realistic');
  const soundToggle = document.getElementById('toggle-sound');
  const settingsBtn = document.getElementById('settings-btn');
  const settingsDialog = document.getElementById('settings-dialog');
  const settingsClose = document.getElementById('settings-close');
  const manualInput = document.getElementById('manual-key-input');
  const manualBtn = document.getElementById('manual-check-btn');
  const manualResult = document.getElementById('manual-result');

  let realisticMode = false;
  realisticToggle.addEventListener('change', (event) => {
    realisticMode = event.target.checked;
    if (realisticMode) {
      classic.hide();
      realistic.show();
    } else {
      realistic.hide();
      classic.show();
    }
  });

  setMuted(!soundToggle.checked);
  soundToggle.addEventListener('change', (event) => {
    setMuted(!event.target.checked);
  });

  settingsBtn.addEventListener('click', () => {
    if (typeof settingsDialog.showModal === 'function') {
      settingsDialog.showModal();
    } else {
      settingsDialog.setAttribute('open', '');
    }
  });
  settingsClose.addEventListener('click', () => settingsDialog.close());

  function setManualResult(text, kind) {
    manualResult.textContent = text;
    manualResult.classList.remove('ok', 'fail', 'err');
    if (kind) manualResult.classList.add(kind);
  }

  async function onManualCheck() {
    setManualResult('', null);
    const raw = manualInput.value.trim();
    if (!raw) {
      setManualResult('Enter a private key first.', 'err');
      return;
    }

    let parsed;
    try {
      parsed = parsePrivKey(raw);
    } catch (error) {
      setManualResult(error.message, 'err');
      return;
    }

    setManualResult('Checking XRPL account…', null);

    try {
      const derived = deriveAll(parsed.privKey);
      const account = await fetchAccountInfo(derived.classicAddress);
      setLedgerIndex(account.ledgerIndex);

      if (account.active) {
        log.append(
          `manual: ${shorten(derived.classicAddress)} -> activated (${formatXrp(account.balanceDrops)} XRP)`
        );
        setManualResult(
          `Activated account: ${formatXrp(account.balanceDrops)} XRP. Opening dialog…`,
          'ok'
        );
        settingsDialog.close();
        winDialog.show({
          classicAddress: derived.classicAddress,
          privateKeyHex: derived.privateKeyHex,
          publicKeyHex: derived.publicKeyHex,
          balanceDrops: account.balanceDrops,
        });
      } else {
        log.append(`manual: ${shorten(derived.classicAddress)} -> inactive`);
        setManualResult(`Inactive account: ${derived.classicAddress}`, 'fail');
      }
    } catch (error) {
      setManualResult(`Error: ${error.message}`, 'err');
    }
  }

  manualBtn.addEventListener('click', onManualCheck);
  manualInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onManualCheck();
    }
  });

  let busy = false;

  async function onPull() {
    if (busy) return;

    busy = true;
    pullBtn.disabled = true;
    unlock();
    sfx.lever();

    const reels = realisticMode ? realistic : classic;
    reels.startSpin();

    try {
      const [result] = await Promise.all([
        spin(),
        new Promise((resolve) => setTimeout(resolve, ANIMATION_MIN_MS)),
      ]);

      log.append(
        `key=${shorten(result.privateKeyHex, 6)} addr=${shorten(result.classicAddress, 6)}`
      );
      setLedgerIndex(result.ledgerIndex);

      if (realisticMode) {
        await realistic.stopSpin(result.privateKeyHex, result.win);
      } else {
        await classic.stopSpin(result.win);
      }

      if (result.win) {
        log.append(
          `MATCH: ${result.classicAddress} (${formatXrp(result.balanceDrops)} XRP)`
        );
        sfx.win();
        winDialog.show(result);
      } else {
        log.append('→ inactive account');
        sfx.lose();
      }
    } catch (error) {
      if (realisticMode) {
        await realistic.stopSpin('0'.repeat(64), null);
      } else {
        await classic.stopSpin(null);
      }
      log.append(`RPC error: ${error.message}`);
    } finally {
      busy = false;
      pullBtn.disabled = false;
    }
  }

  pullBtn.addEventListener('click', onPull);
  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Space') return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (settingsDialog.open || document.getElementById('win-dialog').open) return;
    event.preventDefault();
    onPull();
  });

  try {
    const probe = await probeLedgerIndex();
    if (probe.ledgerIndex !== null) {
      setLedgerIndex(probe.ledgerIndex);
      log.append(
        `RPC probe OK: ledger ${formatNumber(probe.ledgerIndex)} (${probe.serverState ?? 'unknown state'}).`
      );
    }
  } catch (error) {
    document.getElementById('stat-ledger').textContent = 'Unavailable';
    document.getElementById('stat-ledger-sub').textContent = 'probe failed; spins still work';
    log.append(`RPC probe failed: ${error.message}`);
  }
}

main().catch((error) => {
  console.error(error);
  const log = document.getElementById('log');
  if (log) log.value = `Error: ${error.message}`;
});
